/**
 * Address Autocomplete Module for Express Checkout
 *
 * Provides Radar API-based address autocomplete functionality for PeachPay express checkout.
 * Uses PeachPayCustomer API for form field updates and PeachPayOrder for validation.
 */

import {Feature} from '../reducers/environmentReducer';
import {$qs} from '../../../@shared/ts/dom';
import {FeatureFlag} from '../../../@type/features';
import {radarAddressAutocomplete, type RadarAddress} from '../../../@shared/ts/radar-auto-complete';
import {PeachPayCustomer} from '../reducers/peachPayCustomerReducer';
import {PeachPayOrder} from '../reducers/peachPayOrderReducer';

type AutocompleteInstance = {
	input: HTMLInputElement;
	type: 'billing' | 'shipping';
	dropdown: HTMLDivElement;
	selectedIndex: number;
	suggestions: RadarAddress[];
	destroy: () => void;
};

const autocompleteInstances = new Map<string, AutocompleteInstance>();

/**
 * Checks if address autocomplete should be enabled based on feature flags
 */
function shouldEnableAddressAutocomplete() {
	if (Feature.enabled(FeatureFlag.ADDRESS_AUTOCOMPLETE)) {
		const activeLocations = Feature.metadata(FeatureFlag.ADDRESS_AUTOCOMPLETE, 'active_locations');

		if (activeLocations === 'default' || activeLocations === 'express_checkout_only') {
			return true;
		}
	}

	return false;
}

/**
 * Sets up autocomplete for a specific input field in express checkout
 */
function setupAutocomplete(input: HTMLInputElement, type: 'billing' | 'shipping'): void {
	const fieldId = `${type}_address_1`;

	// Skip if already initialized
	if (autocompleteInstances.has(fieldId)) {
		return;
	}

	// Create autocomplete dropdown
	const dropdown = createAutocompleteDropdown();

	// Handle input events
	const handleInput = async (event: Event) => {
		const target = event.target as HTMLInputElement;
		const query = target.value.trim();

		if (query.length < 3) {
			hideAutocompleteDropdown(dropdown);
			return;
		}

		// Show loading state
		showLoadingState(dropdown, input);

		void (async () => {
			try {
				const suggestions = await radarAddressAutocomplete(query);
				const instance = autocompleteInstances.get(fieldId);
				if (instance) {
					instance.suggestions = suggestions;
					instance.selectedIndex = -1;
				}

				if (suggestions.length > 0) {
					showAutocompleteDropdown(dropdown, suggestions, input, type);
				} else {
					showNoResultsState(dropdown, input);
				}
			} catch (error: unknown) {
				console.error('[PeachPay Autocomplete] Error fetching suggestions:', error);
				showNoResultsState(dropdown, input);
			}
		})();
	};

	// Handle keyboard navigation
	const handleKeyDown = (event: KeyboardEvent) => {
		const instance = autocompleteInstances.get(fieldId);
		if (!instance || !dropdown.classList.contains('pp-autocomplete-visible')) {
			return;
		}

		switch (event.key) {
			case 'ArrowDown': {
				event.preventDefault();
				instance.selectedIndex = Math.min(instance.selectedIndex + 1, instance.suggestions.length - 1);
				updateSelectedSuggestion(dropdown, instance.selectedIndex);
				break;
			}

			case 'ArrowUp': {
				event.preventDefault();
				instance.selectedIndex = Math.max(instance.selectedIndex - 1, -1);
				updateSelectedSuggestion(dropdown, instance.selectedIndex);
				break;
			}

			case 'Enter': {
				event.preventDefault();
				if (instance.selectedIndex >= 0 && instance.selectedIndex < instance.suggestions.length) {
					const selectedAddress = instance.suggestions[instance.selectedIndex];
					if (selectedAddress) {
						void selectAddress(selectedAddress, type);
					}
				}

				break;
			}

			case 'Escape': {
				hideAutocompleteDropdown(dropdown);
				break;
			}

			default: {
				break;
			}
		}
	};

	// Handle focus/blur
	const handleFocus = () => {
		const instance = autocompleteInstances.get(fieldId);
		if (instance && instance.suggestions.length > 0 && input.value.trim().length >= 3) {
			showAutocompleteDropdown(dropdown, instance.suggestions, input, type);
		}
	};

	const handleBlur = () => {
		// Delay to allow click events on dropdown to fire
		setTimeout(() => {
			hideAutocompleteDropdown(dropdown);
		}, 200);
	};

	input.addEventListener('input', handleInput);
	input.addEventListener('keydown', handleKeyDown);
	input.addEventListener('focus', handleFocus);
	input.addEventListener('blur', handleBlur);

	// Store instance for cleanup
	autocompleteInstances.set(fieldId, {
		input,
		type,
		dropdown,
		selectedIndex: -1,
		suggestions: [],
		destroy() {
			input.removeEventListener('input', handleInput);
			input.removeEventListener('keydown', handleKeyDown);
			input.removeEventListener('focus', handleFocus);
			input.removeEventListener('blur', handleBlur);
		},
	});
}

/**
 * Creates the autocomplete dropdown element
 */
function createAutocompleteDropdown(): HTMLDivElement {
	const dropdown = document.createElement('div');
	dropdown.className = 'pp-autocomplete-dropdown';
	dropdown.style.cssText = `
		position: absolute;
		background: white;
		border: 1px solid #ccc;
		border-radius: 4px;
		box-shadow: 0 2px 10px rgba(0,0,0,0.1);
		max-height: 300px;
		overflow-y: auto;
		z-index: 10000;
		display: none;
	`;
	document.body.append(dropdown);
	return dropdown;
}

/**
 * Shows loading state while fetching address suggestions from the API
 */
function showLoadingState(dropdown: HTMLDivElement, input: HTMLInputElement): void {
	dropdown.innerHTML = '';
	const loadingItem = document.createElement('div');
	loadingItem.className = 'pp-autocomplete-loading';
	loadingItem.style.cssText = `
		padding: 15px;
		text-align: center;
		color: #666;
		font-style: italic;
	`;
	loadingItem.textContent = 'Searching addresses...';
	dropdown.append(loadingItem);

	// Position dropdown below input
	const rect = input.getBoundingClientRect();
	dropdown.style.top = `${rect.bottom + window.scrollY}px`;
	dropdown.style.left = `${rect.left + window.scrollX}px`;
	dropdown.style.width = `${rect.width}px`;
	dropdown.style.display = 'block';
	dropdown.classList.add('pp-autocomplete-visible');
}

/**
 * Shows "no results found" message when the search returns empty results
 */
function showNoResultsState(dropdown: HTMLDivElement, input: HTMLInputElement): void {
	dropdown.innerHTML = '';
	const noResultsItem = document.createElement('div');
	noResultsItem.className = 'pp-autocomplete-no-results';
	noResultsItem.style.cssText = `
		padding: 15px;
		text-align: center;
		color: #999;
		font-style: italic;
	`;
	noResultsItem.textContent = 'No addresses found';
	dropdown.append(noResultsItem);

	// Position dropdown below input
	const rect = input.getBoundingClientRect();
	dropdown.style.top = `${rect.bottom + window.scrollY}px`;
	dropdown.style.left = `${rect.left + window.scrollX}px`;
	dropdown.style.width = `${rect.width}px`;
	dropdown.style.display = 'block';
	dropdown.classList.add('pp-autocomplete-visible');
}

/**
 * Displays the autocomplete dropdown with address suggestions
 */
function showAutocompleteDropdown(
	dropdown: HTMLDivElement,
	suggestions: RadarAddress[],
	input: HTMLInputElement,
	type: 'billing' | 'shipping',
): void {
	dropdown.innerHTML = '';
	for (const [index, suggestion] of suggestions.entries()) {
		const item = document.createElement('div');
		item.className = 'pp-autocomplete-item';
		item.style.cssText = `
			padding: 10px;
			cursor: pointer;
			border-bottom: 1px solid #eee;
		`;
		item.textContent = suggestion.formattedAddress;
		item.addEventListener('mouseenter', () => {
			const instance = autocompleteInstances.get(`${type}_address_1`);
			if (instance) {
				instance.selectedIndex = index;
				updateSelectedSuggestion(dropdown, index);
			}
		});
		item.addEventListener('click', () => {
			void selectAddress(suggestion, type);
		});
		dropdown.append(item);
	}

	// Position dropdown below input
	const rect = input.getBoundingClientRect();
	dropdown.style.top = `${rect.bottom + window.scrollY}px`;
	dropdown.style.left = `${rect.left + window.scrollX}px`;
	dropdown.style.width = `${rect.width}px`;
	dropdown.style.display = 'block';
	dropdown.classList.add('pp-autocomplete-visible');
}

/**
 * Hides the autocomplete dropdown
 */
function hideAutocompleteDropdown(dropdown: HTMLDivElement): void {
	dropdown.style.display = 'none';
	dropdown.classList.remove('pp-autocomplete-visible');
}

/**
 * Updates the selected suggestion highlight
 */
function updateSelectedSuggestion(dropdown: HTMLDivElement, selectedIndex: number): void {
	const items = dropdown.querySelectorAll('.pp-autocomplete-item');
	for (const [index, item] of items.entries()) {
		if (index === selectedIndex) {
			(item as HTMLElement).style.backgroundColor = '#f0f0f0';
		} else {
			(item as HTMLElement).style.backgroundColor = 'white';
		}
	}
}

/**
 * Selects an address and fills in the form
 */
async function selectAddress(address: RadarAddress, type: 'billing' | 'shipping'): Promise<void> {
	const instance = autocompleteInstances.get(`${type}_address_1`);
	if (instance) {
		hideAutocompleteDropdown(instance.dropdown);
	}

	// Set the input value
	const input = $qs<HTMLInputElement>(`#pp-${type}-form [name="${type}_address_1"]`);
	if (input) {
		input.value = address.formattedAddress;
	}

	// Fill in address using PeachPayCustomer API
	void fillInAddress(address, type);
}

/**
 * Cleans up all autocomplete instances
 */
function cleanupAutocomplete(): void {
	for (const instance of autocompleteInstances.values()) {
		instance.destroy();
		instance.dropdown.remove();
	}

	autocompleteInstances.clear();
}

/**
 * Initializes address autocomplete functionality for express checkout.
 *
 * Sets up Radar API autocomplete for billing and shipping address fields.
 * Uses PeachPayCustomer API for form updates and PeachPayOrder for validation.
 */
export function initAddressAutocomplete(): void {
	if (!shouldEnableAddressAutocomplete()) {
		return;
	}

	const billingInput = $qs<HTMLInputElement>('#pp-billing-form [name="billing_address_1"]');
	if (billingInput) {
		setupAutocomplete(billingInput, 'billing');
	}

	const shippingInput = $qs<HTMLInputElement>('#pp-shipping-form [name="shipping_address_1"]');
	if (shippingInput) {
		setupAutocomplete(shippingInput, 'shipping');
	}

	// Clean up on page unload
	window.addEventListener('beforeunload', cleanupAutocomplete);
}

/**
 * Fills in address fields using PeachPayCustomer API.
 *
 * Maps Radar address data to express checkout form fields using the PeachPayCustomer
 * API which handles form updates and triggers necessary validation.
 *
 * @param address - The selected Radar address
 * @param addressType - Address type ('billing' or 'shipping')
 */
async function fillInAddress(address: RadarAddress, addressType: 'billing' | 'shipping'): Promise<void> {
	const form = addressType === 'billing' ? PeachPayCustomer.billing : PeachPayCustomer.shipping;

	if (!address) {
		return;
	}

	// Set country first so state field can update accordingly
	if (address.countryCode) {
		form.country(address.countryCode, true);
	}

	// Set address line 1 (use formatted address or construct from components)
	const addressLine1 = address.formattedAddress ?? (address.number && address.street ? `${address.number} ${address.street}`.trim() : address.street ?? '');
	if (addressLine1) {
		form.address1(addressLine1, true);
	}

	// Set address line 2 (apartment/unit) - Radar doesn't provide this separately, so leave empty
	form.address2('', true);

	// Set city
	if (address.city) {
		form.city(address.city, true);
	}

	// Set postal code
	if (address.postalCode) {
		form.postal(address.postalCode, true);
	}

	// Set state/province
	const state = (address.stateCode ?? '').split('.').join('');
	if (state) {
		form.state(state, true);
	}

	// Report validity to trigger form validation
	await (addressType === 'billing' ? PeachPayOrder.billing : PeachPayOrder.shipping)?.reportValidity();
}
